import { lazy, Suspense, useState, useEffect } from 'react';
import clsx from 'clsx';
import { WandSparklesIcon } from 'lucide-react';

// Only import custom icons statically since these are fixed
const customIcons = {
  instagram: lazy(() => import('../assets/instagram-brands-solid.svg?react')),
  linkedin: lazy(() => import('../assets/linkedin-brands-solid.svg?react')),
  reddit: lazy(() => import('../assets/reddit-alien-brands-solid.svg?react')),
  facebook: lazy(() => import('../assets/square-facebook-brands-solid.svg?react')),
  etsy: lazy(() => import('../assets/etsy-brands-solid.svg?react')),
  pinterest: lazy(() => import('../assets/pinterest-brands-solid.svg?react')),
  'x-twitter': lazy(() => import('../assets/x-twitter-brands-solid.svg?react')),
  bluesky: lazy(() => import('../assets/bluesky-brands-solid.svg?react')),
  tiktok: lazy(() => import('../assets/tiktok-brands-solid.svg?react')),
  threads: lazy(() => import('../assets/threads-brands-solid.svg?react')),
  youtube: lazy(() => import('../assets/youtube-brands-solid.svg?react')),
};

export function Icon({ icon, size = 6, className }: { icon: string; size?: number; className?: string }) {
  const [LucideIcon, setLucideIcon] = useState<React.ComponentType<any> | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (icon in customIcons) return;

    setLoading(true);
    // Convert icon name to PascalCase and append 'Icon'
    const pascalCaseIcon =
      icon
        .split(/[-_\s]+/)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join('') + 'Icon';

    // Dynamically import from lucide-react
    import('lucide-react')
      .then((module: any) => {
        setLucideIcon(() => module[pascalCaseIcon] || WandSparklesIcon);
        setLoading(false);
      })
      .catch(() => {
        setLucideIcon(WandSparklesIcon);
        setLoading(false);
      });
  }, [icon]);

  // Handle custom icons
  if (icon in customIcons) {
    const CustomIcon = customIcons[icon as keyof typeof customIcons];
    return (
      <Suspense fallback={<WandSparklesIcon className={clsx(`w-${size} h-${size}`, className)} />}>
        <CustomIcon className={clsx(`w-${size} h-${size}`, className)} />
      </Suspense>
    );
  }

  // Show loading state or fallback for Lucide icons
  if (loading || !LucideIcon) {
    return <WandSparklesIcon className={clsx(`w-${size} h-${size}`, className)} />;
  }

  // Render the loaded Lucide icon
  return <LucideIcon className={clsx(`w-${size} h-${size}`, className)} />;
}
